import { useEffect, useRef, useState } from "react";

/**
 * Custom hook for mobile message gestures
 * - Swipe right to reply
 * - Long press for menu
 */
const useMessageGestures = (message, onReply, onShowMenu) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const longPressTimer = useRef(null);
  const isSwipping = useRef(false);
  const elementRef = useRef(null);

  const SWIPE_THRESHOLD = 80; // pixels to trigger reply
  const LONG_PRESS_DURATION = 500; // ms for long press
  const SWIPE_SENSITIVITY = 30; // minimum horizontal movement to detect swipe

  const handleTouchStart = (e) => {
    // Only handle on mobile
    if (window.innerWidth > 768) return;

    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    touchStartTime.current = Date.now();
    isSwipping.current = false;

    // Start long press timer
    longPressTimer.current = setTimeout(() => {
      if (!isSwipping.current) {
        // Trigger long press - show menu
        navigator.vibrate?.(50); // Haptic feedback if available
        onShowMenu?.();
      }
    }, LONG_PRESS_DURATION);
  };

  const handleTouchMove = (e) => {
    if (window.innerWidth > 768) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = Math.abs(touch.clientY - touchStartY.current);

    // Cancel long press if moving
    if (Math.abs(deltaX) > 10 || deltaY > 10) {
      clearTimeout(longPressTimer.current);
    }

    // Detect horizontal swipe (not vertical scroll)
    if (Math.abs(deltaX) > SWIPE_SENSITIVITY && deltaY < 30) {
      isSwipping.current = true;

      // Only allow right swipe (positive deltaX) up to threshold
      if (deltaX > 0 && deltaX <= SWIPE_THRESHOLD * 1.5) {
        setSwipeOffset(deltaX);
        e.preventDefault(); // Prevent scrolling while swiping
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (window.innerWidth > 768) return;

    clearTimeout(longPressTimer.current);

    // Check if swipe exceeded threshold
    if (swipeOffset >= SWIPE_THRESHOLD) {
      navigator.vibrate?.(30); // Haptic feedback
      onReply?.(message);
    }

    // Reset swipe
    setSwipeOffset(0);
    isSwipping.current = false;
  };

  const handleTouchCancel = () => {
    clearTimeout(longPressTimer.current);
    setSwipeOffset(0);
    isSwipping.current = false;
  };

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    element.addEventListener("touchstart", handleTouchStart, { passive: true });
    element.addEventListener("touchmove", handleTouchMove, { passive: false });
    element.addEventListener("touchend", handleTouchEnd, { passive: true });
    element.addEventListener("touchcancel", handleTouchCancel, {
      passive: true,
    });

    return () => {
      element.removeEventListener("touchstart", handleTouchStart);
      element.removeEventListener("touchmove", handleTouchMove);
      element.removeEventListener("touchend", handleTouchEnd);
      element.removeEventListener("touchcancel", handleTouchCancel);
      clearTimeout(longPressTimer.current);
    };
  }, [swipeOffset, message]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    elementRef,
    swipeOffset,
    swipeProgress: Math.min(swipeOffset / SWIPE_THRESHOLD, 1), // 0 to 1
  };
};

export default useMessageGestures;
